// src/context/ThemeContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { usersAPI } from '../services/api';

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [theme, setTheme] = useState('light'); // default to light mode
  const [loading, setLoading] = useState(true);

  // Initialize theme on mount
  useEffect(() => {
    initializeTheme();
  }, []);

  // Sync theme when user changes (login/logout)
  useEffect(() => {
    if (user && user.themePreference) {
      // User is logged in and has a saved preference
      applyTheme(user.themePreference);
    }
  }, [user]);

  const initializeTheme = () => {
    // Priority order:
    // 1. User's saved preference (from backend, set via useEffect above)
    // 2. LocalStorage preference
    // 3. System preference
    // 4. Default to 'light'

    const savedTheme = localStorage.getItem('theme');

    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      applyTheme(savedTheme);
    } else {
      // Auto-detect system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const systemTheme = prefersDark ? 'dark' : 'light';
      applyTheme(systemTheme);
    }

    setLoading(false);
  };

  const applyTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);

    // Apply theme class to html element for Tailwind dark mode
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const toggleTheme = async (newTheme) => {
    applyTheme(newTheme);

    // If user is logged in, save preference to backend
    if (user) {
      try {
        await usersAPI.updateThemePreference(newTheme);
      } catch (error) {
        console.error('Failed to save theme preference to server:', error);
        // Continue anyway - local preference is still saved
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for easy theme access
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
